import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Check, X, ArrowLeft, AlertTriangle } from "lucide-react";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import { api } from "@/lib/api";

const formatDateTime = (iso) =>
    new Date(iso).toLocaleString("fr-CH", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Zurich",
    });

export default function Cancel() {
    const { token } = useParams();
    const [appt, setAppt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        api
            .get(`/appointments/cancel/${token}`)
            .then((r) => setAppt(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Lien invalide ou expiré"))
            .finally(() => setLoading(false));
    }, [token]);

    const submit = async () => {
        setSubmitting(true);
        try {
            await api.post(`/appointments/cancel/${token}`, { reason: reason || null });
            setDone(true);
        } catch (e) {
            setError(e?.response?.data?.detail || "Échec de l'annulation");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-brand-cream" data-testid="cancel-page">
            <Navbar />
            <main className="pt-32 pb-24">
                <div className="max-w-[680px] mx-auto px-6 lg:px-10">
                    <Link to="/" className="inline-flex items-center gap-2 text-sm text-brand-primary mb-8 ll-link-underline">
                        <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
                    </Link>

                    {loading && (
                        <div className="text-center py-16">
                            <Loader2 className="h-8 w-8 animate-spin text-brand-primary mx-auto" />
                        </div>
                    )}

                    {!loading && error && !appt && (
                        <div className="bg-white border border-brand-line p-10 text-center">
                            <div className="h-14 w-14 rounded-full bg-red-50 text-red-600 inline-flex items-center justify-center mb-6">
                                <AlertTriangle className="h-6 w-6" />
                            </div>
                            <h1 className="font-display text-3xl text-brand-ink mb-3">Lien invalide</h1>
                            <p className="font-sans text-brand-ink/65 mb-6">{error}</p>
                            <Link to="/rendez-vous" className="ll-btn ll-btn-primary">Reprendre un RDV</Link>
                        </div>
                    )}

                    {!loading && appt && !done && appt.status === "cancelled" && (
                        <div className="bg-white border border-brand-line p-10 text-center">
                            <div className="h-14 w-14 rounded-full bg-brand-mint text-brand-primary inline-flex items-center justify-center mb-6">
                                <Check className="h-6 w-6" />
                            </div>
                            <h1 className="font-display text-3xl text-brand-ink mb-3">Déjà annulé</h1>
                            <p className="font-sans text-brand-ink/65 mb-6">Ce rendez-vous est déjà annulé.</p>
                            <Link to="/rendez-vous" className="ll-btn ll-btn-primary">Reprendre un RDV</Link>
                        </div>
                    )}

                    {!loading && appt && !done && appt.status === "confirmed" && (
                        <div data-testid="cancel-form">
                            <p className="ll-eyebrow text-brand-primary mb-4">Annuler un rendez-vous</p>
                            <h1 className="font-display text-3xl md:text-4xl leading-tight text-brand-ink mb-6">
                                Êtes-vous sûr·e d'annuler ce rendez-vous ?
                            </h1>

                            <div className="bg-white border border-brand-line p-6 mb-6 space-y-3 font-sans">
                                <Row label="Type" value={appt.type_name} />
                                <Row label="Quand" value={formatDateTime(appt.start_at)} />
                                <Row label="Pharmacien·ne" value={appt.pharmacist_name} />
                                <Row label="Au nom de" value={appt.customer_name} />
                            </div>

                            <div className="bg-white border border-brand-line p-6 mb-6">
                                <label className="ll-eyebrow text-brand-ink/60 block mb-2">
                                    Raison (optionnel)
                                </label>
                                <textarea
                                    rows={3}
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Une note pour la pharmacie ?"
                                    className="w-full bg-transparent border-b border-brand-ink/30 focus:border-brand-primary outline-none py-2 font-sans resize-none"
                                    data-testid="cancel-reason-input"
                                />
                            </div>

                            {error && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm font-sans rounded">
                                    {error}
                                </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={submit}
                                    disabled={submitting}
                                    className="ll-btn ll-btn-primary flex-1 justify-center disabled:opacity-60"
                                    data-testid="cancel-confirm-btn"
                                >
                                    {submitting ? (
                                        <><Loader2 className="h-4 w-4 animate-spin" /> Annulation en cours…</>
                                    ) : (
                                        <><X className="h-4 w-4" /> Confirmer l'annulation</>
                                    )}
                                </button>
                                <Link to="/" className="ll-btn ll-btn-ghost flex-1 justify-center">
                                    Garder mon rendez-vous
                                </Link>
                            </div>
                        </div>
                    )}

                    {done && (
                        <div className="bg-white border border-brand-line p-10 text-center" data-testid="cancel-done">
                            <div className="h-14 w-14 rounded-full bg-brand-mint text-brand-primary inline-flex items-center justify-center mb-6">
                                <Check className="h-6 w-6" />
                            </div>
                            <h1 className="font-display text-3xl text-brand-ink mb-3">Rendez-vous annulé</h1>
                            <p className="font-sans text-brand-ink/65 mb-6">
                                C'est confirmé. Une notification a été envoyée à la pharmacie.
                                Au plaisir de vous accueillir une prochaine fois.
                            </p>
                            <Link to="/rendez-vous" className="ll-btn ll-btn-primary">
                                Reprendre un rendez-vous
                            </Link>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}

const Row = ({ label, value }) => (
    <div className="flex justify-between items-baseline gap-4 border-b last:border-b-0 border-brand-line py-2">
        <span className="text-sm text-brand-ink/55">{label}</span>
        <span className="text-brand-ink text-right capitalize">{value}</span>
    </div>
);
